import { useState } from 'react';
import { getScoreColor } from '../utils/scoring';

const JOURNEY_PHASES = [
  { phase: 'Pre-Visit', color: '#60a5fa', weight: 0.75, touchpoints: [
    { id: 'awareness', label: 'Awareness / Discovery', icon: '🔍' },
    { id: 'googleSearch', label: 'Google Search & Listing', icon: '🔎' },
    { id: 'websiteVisit', label: 'Website Experience', icon: '💻' },
    { id: 'socialScroll', label: 'Social Media Presence', icon: '📱' },
    { id: 'reviewRead', label: 'Reading Reviews', icon: '⭐' },
    { id: 'reservation', label: 'Booking / Ordering', icon: '📞' },
    { id: 'deliveryPresence', label: 'Third-Party Platforms', icon: '📦' },
  ]},
  { phase: 'Arrival', color: '#C9A668', weight: 1.0, touchpoints: [
    { id: 'neighborhood', label: 'Neighborhood & Approach', icon: '🏙️' },
    { id: 'parkingValet', label: 'Parking / Valet / Access', icon: '🅿️' },
    { id: 'exterior', label: 'Exterior & Signage', icon: '🪧' },
    { id: 'entrance', label: 'Entrance Experience', icon: '🚪' },
    { id: 'hostGreeting', label: 'Host / First Greeting', icon: '👋' },
    { id: 'waitExperience', label: 'Wait Experience', icon: '⏱️' },
    { id: 'pickupHandoff', label: 'Pickup / To-Go Handoff', icon: '🛍️' },
    { id: 'seatingWalk', label: 'Walk to Seat', icon: '🚶' },
  ]},
  { phase: 'The Setting', color: '#34d399', weight: 1.0, touchpoints: [
    { id: 'tableFirst', label: 'Seat First Impression', icon: '🪑' },
    { id: 'ambiance', label: 'Ambiance & Energy', icon: '✨' },
    { id: 'serverIntro', label: 'Server / Staff Introduction', icon: '🤝' },
    { id: 'menuPresentation', label: 'Menu Presentation', icon: '📖' },
    { id: 'menuAccuracy', label: 'Menu Accuracy', icon: '✅' },
    { id: 'waterBread', label: 'Opening Gesture', icon: '💧' },
    { id: 'familyAccommodation', label: 'Family / Group Accommodation', icon: '👨‍👩‍👧' },
  ]},
  { phase: 'Food & Beverage', color: '#fb923c', weight: 1.5, touchpoints: [
    { id: 'firstBeverage', label: 'First Beverage', icon: '🥤' },
    { id: 'appetizerCourse', label: 'Starter / Snack / Shared', icon: '🥗' },
    { id: 'entreeCourse', label: 'Main Course', icon: '🍽️' },
    { id: 'foodPresentation', label: 'Plating & Presentation', icon: '🎨' },
    { id: 'beverageThroughout', label: 'Beverage Service Throughout', icon: '🍷' },
    { id: 'barProgram', label: 'Bar / Beverage Program', icon: '🍸' },
    { id: 'pacing', label: 'Course Pacing', icon: '⏳' },
    { id: 'dessertOffer', label: 'Dessert / Final Course', icon: '🍰' },
  ]},
  { phase: 'Service & Hospitality', color: '#c084fc', weight: 1.5, touchpoints: [
    { id: 'serviceSpeed', label: 'Speed of Service', icon: '⚡' },
    { id: 'attentiveness', label: 'Attentiveness', icon: '👁️' },
    { id: 'checkIns', label: 'Check-ins & Timing', icon: '🔄' },
    { id: 'teamKnowledge', label: 'Team Knowledge', icon: '🧠' },
    { id: 'specialRequests', label: 'Special Requests & Allergies', icon: '✋' },
    { id: 'managerPresence', label: 'Manager / Owner Presence', icon: '👔' },
    { id: 'restroomVisit', label: 'Restroom Experience', icon: '🚻' },
    { id: 'problemRecovery', label: 'Problem Recovery', icon: '🔧' },
  ]},
  { phase: 'Departure', color: '#f87171', weight: 1.0, touchpoints: [
    { id: 'checkPresentation', label: 'Check Presentation', icon: '💳' },
    { id: 'takeawayPackaging', label: 'Takeaway / Leftovers', icon: '📦' },
    { id: 'coatCheckValet', label: 'Coat Check / Valet Return', icon: '🧥' },
    { id: 'farewell', label: 'Farewell', icon: '👋' },
    { id: 'lastImpression', label: 'Last Impression', icon: '🌙' },
  ]},
  { phase: 'Post-Visit', color: '#06b6d4', weight: 0.75, touchpoints: [
    { id: 'followUp', label: 'Follow-Up Communication', icon: '📧' },
    { id: 'loyaltyCRM', label: 'Loyalty / CRM / Retention', icon: '💎' },
    { id: 'socialPrompt', label: 'Social Shareability', icon: '📸' },
    { id: 'reviewResponse', label: 'Review Response Quality', icon: '💬' },
    { id: 'memoryAt48hrs', label: 'The 48-Hour Memory', icon: '🧠' },
    { id: 'wouldReturn', label: 'Would You Return?', icon: '🔄' },
    { id: 'wouldRecommend', label: 'Would You Recommend?', icon: '💬' },
  ]},
];

function ScoreBar({ score, color }) {
  const width = Math.max(0, Math.min(100, score * 10));
  const sc = getScoreColor(score);
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${width}%`, backgroundColor: color || '#C9A668' }} />
      </div>
      <span className={`text-xs font-bold w-8 text-right ${sc.text}`}>{score.toFixed(1)}</span>
    </div>
  );
}

function getTPState(journey, tp) {
  const d = journey[tp.id];
  if (!d) return 'default';
  if (d.notOffered || d.na) return 'notOffered';
  if (d.approved) return 'scored';
  if (d.scored || d.score > 0 || d.aiScore > 0) return 'aiSuggested';
  return 'default';
}

function calcVisitStats(journey) {
  const allTPs = JOURNEY_PHASES.flatMap(p => p.touchpoints);
  const scored = allTPs.filter(t => getTPState(journey, t) === 'scored');
  const notOffered = allTPs.filter(t => getTPState(journey, t) === 'notOffered');
  const evaluated = allTPs.filter(t => getTPState(journey, t) !== 'default');
  // Weighted overall: phase averages weighted by phase.weight
  let weightedSum = 0, totalWeight = 0;
  JOURNEY_PHASES.forEach(phase => {
    const ps = phase.touchpoints.filter(t => getTPState(journey, t) === 'scored');
    if (ps.length > 0) {
      const phaseAvg = ps.reduce((s, t) => s + (journey[t.id]?.score || 0), 0) / ps.length;
      weightedSum += phaseAvg * (phase.weight || 1.0);
      totalWeight += (phase.weight || 1.0);
    }
  });
  const overall = totalWeight > 0 ? weightedSum / totalWeight : 0;
  const phases = JOURNEY_PHASES.map(phase => {
    const ps = phase.touchpoints.filter(t => getTPState(journey, t) === 'scored');
    const avg = ps.length > 0 ? ps.reduce((s, t) => s + (journey[t.id]?.score || 0), 0) / ps.length : 0;
    const pe = phase.touchpoints.filter(t => getTPState(journey, t) !== 'default');
    return { ...phase, avg, scoredCount: pe.length };
  });
  const allScored = allTPs
    .filter(t => getTPState(journey, t) === 'scored')
    .map(t => {
      const d = journey[t.id];
      return { ...t, score: d.score || 0, observation: d.observation ? d.observation : (d.approved ? (d.aiResearch || '') : '') };
    })
    .sort((a, b) => b.score - a.score);
  return { overall, phases, allScored, scoredCount: scored.length, evaluatedCount: evaluated.length, notOfferedCount: notOffered.length, totalCount: allTPs.length };
}

function VisitReport({ visit, visitIndex, isOnly }) {
  const journey = visit.data || {};
  const stats = calcVisitStats(journey);
  const overallColor = getScoreColor(stats.overall);
  const strengths = stats.allScored.filter(t => t.score >= 7).slice(0, 5);
  const focusAreas = [...stats.allScored].sort((a, b) => a.score - b.score).filter(t => t.score < 6).slice(0, 5);

  return (
    <div className={!isOnly ? 'mb-12' : ''}>
      {/* Visit header */}
      {!isOnly && (
        <div className="flex items-center justify-between mb-6 pb-3 border-b border-neutral-800">
          <div>
            <h2 className="font-display text-lg text-brand-gold">{visit.name}{(visit.mealPeriod || visit.seating) ? ` — ${[visit.mealPeriod, visit.seating].filter(Boolean).join(' · ')}` : ''}</h2>
            {(visit.date || visit.createdAt) && <p className="text-[10px] text-neutral-600">{new Date((visit.date || visit.createdAt) + (visit.date ? 'T12:00:00' : '')).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>}
          </div>
          <div className={`w-16 h-16 rounded-xl ${overallColor.bg} flex flex-col items-center justify-center`}>
            <span className={`text-xl font-black ${overallColor.text}`}>{stats.overall > 0 ? stats.overall.toFixed(1) : '—'}</span>
            <span className="text-[7px] text-neutral-500">{stats.scoredCount}/{stats.totalCount}</span>
          </div>
        </div>
      )}

      {/* Phase Summary Bar */}
      <div className="card p-5 mb-6">
        <h3 className="text-sm font-semibold text-white mb-4">Phase Scores</h3>
        <div className="space-y-3">
          {stats.phases.map(p => (
            <div key={p.phase} className="flex items-center gap-3">
              <span className="text-xs font-medium w-24 text-right" style={{ color: p.color }}>{p.phase}</span>
              <ScoreBar score={p.avg} color={p.color} />
              <span className="text-[9px] text-neutral-600 w-12">{p.scoredCount}/{p.touchpoints.length}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Strengths & Focus Areas */}
      {(strengths.length > 0 || focusAreas.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {strengths.length > 0 && (
            <div className="card p-5">
              <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-3">Top Strengths</h3>
              <div className="space-y-2">
                {strengths.map(t => {
                  const sc = getScoreColor(t.score);
                  return (
                    <div key={t.id} className="flex items-start gap-2">
                      <span className={`text-xs font-bold ${sc.text} w-8 flex-shrink-0`}>{t.score.toFixed(1)}</span>
                      <div>
                        <p className="text-xs text-white font-medium">{t.label}</p>
                        {t.observation && <p className="text-[10px] text-neutral-500 mt-0.5">{t.observation}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {focusAreas.length > 0 && (
            <div className="card p-5">
              <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-3">Focus Areas</h3>
              <div className="space-y-2">
                {focusAreas.map(t => {
                  const sc = getScoreColor(t.score);
                  return (
                    <div key={t.id} className="flex items-start gap-2">
                      <span className={`text-xs font-bold ${sc.text} w-8 flex-shrink-0`}>{t.score.toFixed(1)}</span>
                      <div>
                        <p className="text-xs text-white font-medium">{t.label}</p>
                        {t.observation && <p className="text-[10px] text-neutral-500 mt-0.5">{t.observation}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detailed Phase Breakdown */}
      <div className="space-y-6">
        {JOURNEY_PHASES.map(phase => {
          const pScored = phase.touchpoints.filter(t => (journey[t.id]?.score || 0) > 0);
          const phaseAvg = pScored.length > 0
            ? pScored.reduce((s, t) => s + (journey[t.id]?.score || 0), 0) / pScored.length : 0;
          const phaseColor = getScoreColor(phaseAvg);

          return (
            <div key={phase.phase} className="card overflow-hidden">
              <div className="px-5 py-4 flex items-center justify-between" style={{ borderLeft: `4px solid ${phase.color}` }}>
                <div>
                  <h3 className="text-sm font-semibold text-white">{phase.phase}</h3>
                  <p className="text-[10px] text-neutral-500">{pScored.length} of {phase.touchpoints.length} scored</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${phaseColor.bg} flex items-center justify-center`}>
                  <span className={`text-lg font-black ${phaseColor.text}`}>{phaseAvg.toFixed(1)}</span>
                </div>
              </div>
              <div className="divide-y divide-neutral-800/50">
                {phase.touchpoints.filter(tp => {
                  const d = journey[tp.id];
                  return !(d?.notOffered || d?.na); // Hide N/A from client
                }).map(tp => {
                  const data = journey[tp.id] || {};
                  const score = data.score || 0;
                  const sc = getScoreColor(score);
                  const observation = data.observation ? data.observation : (data.approved ? (data.aiResearch || '') : '');

                  return (
                    <div key={tp.id} className="px-5 py-3">
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="text-base">{tp.icon}</span>
                        <span className="text-xs font-medium flex-1 text-white">{tp.label}</span>
                        <div className={`w-10 h-10 rounded-lg ${sc.bg} flex items-center justify-center`}>
                          <span className={`text-sm font-bold ${sc.text}`}>{(data.approved || data.scored || score > 0) ? score.toFixed(1) : '—'}</span>
                        </div>
                      </div>
                      {(data.approved || data.scored || score > 0) && (
                        <div className="ml-8 mb-1">
                          <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${score * 10}%`, backgroundColor: phase.color }} />
                          </div>
                        </div>
                      )}
                      {observation && (
                        <div className="ml-8 mt-1.5">
                          <p className="text-[10px] text-neutral-400 leading-relaxed">{observation}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function GuestJourneyReport({ audit, embedded = false }) {
  const [activeVisit, setActiveVisit] = useState(null); // null = show all
  if (!audit) return null;
  const intake = audit.intake || {};

  // Build visits list — backward compat
  const visits = Array.isArray(audit.guestJourneys) && audit.guestJourneys.length > 0
    ? audit.guestJourneys
    : audit.guestJourney && Object.keys(audit.guestJourney).length > 0
      ? [{ name: 'Visit 1', data: audit.guestJourney }]
      : [];

  if (visits.length === 0) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><p className="text-neutral-500">No guest journey data</p></div>;
  }

  // Overall across all visits
  const allStats = visits.map(v => calcVisitStats(v.data || {}));
  const grandAvg = allStats.filter(s => s.overall > 0).length > 0
    ? allStats.reduce((s, st) => s + st.overall, 0) / allStats.filter(s => s.overall > 0).length : 0;
  const grandColor = getScoreColor(grandAvg);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-black'}>
      <div className={embedded ? 'px-2' : 'max-w-4xl mx-auto py-10 px-4 sm:px-6'}>
        {/* Header — hidden when embedded */}
        {!embedded && (
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-800">
            <div className="flex items-center gap-3">
              <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="NE" className="h-8" />
              <div>
                <h1 className="font-display text-xl text-brand-gold">Visit Audit | Guest Journey</h1>
                <p className="text-xs text-neutral-500">{intake.conceptName} {intake.city ? `— ${intake.city}` : ''}</p>
              </div>
            </div>
            <div className={`w-20 h-20 rounded-2xl ${grandColor.bg} flex flex-col items-center justify-center`}>
              <span className={`text-2xl font-black ${grandColor.text}`}>{grandAvg > 0 ? grandAvg.toFixed(1) : '—'}</span>
              <span className="text-[8px] text-neutral-500 uppercase tracking-wider">{visits.length > 1 ? 'Average' : 'Overall'}</span>
            </div>
          </div>
        )}
        {/* Embedded score header */}
        {embedded && (
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl text-brand-gold">Visit Audit | Guest Journey</h2>
            <div className={`w-16 h-16 rounded-xl ${grandColor.bg} flex flex-col items-center justify-center`}>
              <span className={`text-xl font-black ${grandColor.text}`}>{grandAvg > 0 ? grandAvg.toFixed(1) : '—'}</span>
              <span className="text-[7px] text-neutral-500">{visits.length > 1 ? 'Average' : 'Overall'}</span>
            </div>
          </div>
        )}

        {/* Visit tabs — only show if multiple */}
        {visits.length > 1 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <button
              onClick={() => setActiveVisit(null)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all ${activeVisit === null ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/40 font-semibold' : 'bg-neutral-800/40 text-neutral-500 hover:text-neutral-300 border border-neutral-800'}`}
            >All Visits</button>
            {visits.map((v, i) => {
              const vStats = allStats[i];
              const vc = getScoreColor(vStats.overall);
              return (
                <button
                  key={i}
                  onClick={() => setActiveVisit(i)}
                  className={`text-xs px-3 py-1.5 rounded-lg transition-all ${activeVisit === i ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/40 font-semibold' : 'bg-neutral-800/40 text-neutral-500 hover:text-neutral-300 border border-neutral-800'}`}
                >
                  {v.name}
                  {(v.mealPeriod || v.seating) && <span className="text-[9px] ml-1 opacity-70">{[v.mealPeriod, v.seating].filter(Boolean).join(' · ')}</span>}
                  {v.date && <span className="text-[9px] ml-1 opacity-50">{new Date(v.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                  <span className={`ml-1 ${vc.text}`}>{vStats.overall > 0 ? vStats.overall.toFixed(1) : ''}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Combined averaged view — scores only, no text */}
        {activeVisit === null && visits.length > 1 && (() => {
          // Average scores across all visits per touchpoint
          const allTPs = JOURNEY_PHASES.flatMap(p => p.touchpoints);
          const avgTP = {};
          allTPs.forEach(tp => {
            const scores = visits.map(v => (v.data || {})[tp.id]).filter(d => d && (d.approved || d.scored || d.score > 0) && !d.notOffered && !d.na);
            if (scores.length > 0) {
              avgTP[tp.id] = scores.reduce((s, d) => s + (d.score || 0), 0) / scores.length;
            }
          });

          // Phase averages for summary
          const phaseAvgs = JOURNEY_PHASES.map(phase => {
            const phaseTPs = phase.touchpoints.filter(tp => avgTP[tp.id] !== undefined);
            const avg = phaseTPs.length > 0 ? phaseTPs.reduce((s, tp) => s + avgTP[tp.id], 0) / phaseTPs.length : 0;
            return { ...phase, avg };
          });

          return (
            <div className="space-y-4">
              {/* Phase score summary */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Phase Averages</h3>
                <div className="space-y-3">
                  {phaseAvgs.map(p => {
                    const sc = getScoreColor(p.avg);
                    return (
                      <div key={p.phase} className="flex items-center gap-3">
                        <span className="text-xs font-medium w-24 text-right" style={{ color: p.color }}>{p.phase}</span>
                        <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, p.avg * 10))}%`, backgroundColor: p.color }} />
                        </div>
                        <span className={`text-xs font-bold w-8 text-right ${sc.text}`}>{p.avg.toFixed(1)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {JOURNEY_PHASES.map(phase => {
                const phaseTPs = phase.touchpoints.filter(tp => avgTP[tp.id] !== undefined);
                const phaseAvg = phaseTPs.length > 0 ? phaseTPs.reduce((s, tp) => s + avgTP[tp.id], 0) / phaseTPs.length : 0;
                const phaseColor = getScoreColor(phaseAvg);

                return (
                  <div key={phase.phase} className="card overflow-hidden">
                    <div className="px-5 py-3 flex items-center justify-between" style={{ borderLeft: `4px solid ${phase.color}` }}>
                      <h3 className="text-sm font-semibold text-white">{phase.phase}</h3>
                      <div className={`w-11 h-11 rounded-xl ${phaseColor.bg} flex items-center justify-center`}>
                        <span className={`text-base font-black ${phaseColor.text}`}>{phaseAvg.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-neutral-800/50">
                      {phase.touchpoints.filter(tp => avgTP[tp.id] !== undefined).map(tp => {
                        const avg = avgTP[tp.id];
                        const sc = getScoreColor(avg);
                        return (
                          <div key={tp.id} className="px-5 py-2.5 flex items-center gap-3">
                            <span className="text-sm">{tp.icon}</span>
                            <span className="text-xs text-white font-medium flex-1">{tp.label}</span>
                            {avg !== undefined ? (
                              <>
                                <div className="w-24 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${avg * 10}%`, backgroundColor: phase.color }} />
                                </div>
                                <span className={`text-xs font-bold w-8 text-right ${sc.text}`}>{avg.toFixed(1)}</span>
                              </>
                            ) : (
                              <span className="text-[9px] text-neutral-700">—</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Single visit detail or single-visit audit */}
        {activeVisit !== null ? (
          <VisitReport visit={visits[activeVisit]} visitIndex={activeVisit} isOnly={true} />
        ) : visits.length === 1 ? (
          <VisitReport visit={visits[0]} visitIndex={0} isOnly={true} />
        ) : null}

        {/* Footer — hidden when embedded */}
        {!embedded && (
          <div className="text-center pt-8 mt-8 border-t border-neutral-800">
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="Next Experience Group" className="h-6 w-auto" />
              <span className="text-sm font-bold text-white">What's NEXT</span>
            </div>
            <p className="text-xs text-neutral-500">Visit Audit | Guest Journey — Next Experience Group</p>
          </div>
        )}
      </div>
    </div>
  );
}
